import { AnyAction } from 'redux';
import { FormikErrors } from 'formik';
import { HandledError, PaginatedResponse } from '@synergy/api';
import { Media } from '@synergy/models';
import lodashSet from 'lodash/set';

import { BankAccountErrorCode, BankAccountErrorCodeMessageId } from '../../../../models/bank-account-error-code';
import { BankAccountFormValues } from './../../../../models/bank-account-form-values';
import { EmailErrorCode, EmailErrorCodeMessageId } from '../../../../models/email-error-code';
import {
    SUPPLIER_DETAIL_BANK_ACCOUNT_CREATED,
    SUPPLIER_DETAIL_BANK_ACCOUNT_DELETE_FINISHED,
    SUPPLIER_DETAIL_BANK_ACCOUNT_ERROR,
    SUPPLIER_DETAIL_BANK_ACCOUNT_UPDATED,
    SUPPLIER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED,
    SUPPLIER_DETAIL_FTP_ADD_SUCCESS,
    SUPPLIER_DETAIL_FTP_USER_CLEAR,
    SUPPLIER_DETAIL_NEW_FTP_PASSWORD_SAVE,
    SUPPLIER_DETAIL_NEW_FTP_PASSWORD_SAVE_SUCCESS,
    SUPPLIER_DETAIL_OVERVIEW_ERROR,
    SUPPLIER_DETAIL_OVERVIEW_RECEIVE_DETAILS,
    SUPPLIER_DETAIL_OVERVIEW_REMOVED_PROFILE_IMAGE,
    SUPPLIER_DETAIL_OVERVIEW_UPDATE,
    SUPPLIER_DETAIL_OVERVIEW_UPLOAD_PROFILE_FINISHED,
    SUPPLIER_DETAIL_WHITELISTED_CUSTOMERS_RECEIVE,
} from './supplier-detail-overview-actions';
import {
    SUPPLIER_DETAIL_TOGGLE_HIDE_PRODUCTS,
    SUPPLIER_DETAIL_TOGGLE_LOCK_SUPPLIER,
} from '../../store/supplier-detail-actions';
import { SupplierDetailOverviewFormValues } from './../models/supplier-detail-overview-form-values';
import { WhitelistedCustomer } from './../models/whitelisted-customer';

export interface SupplierDetailOverviewReducerState {
    supplier?: SupplierDetailOverviewFormValues;
    whitelistedCustomer: PaginatedResponse<WhitelistedCustomer>;
    bankAccounts: BankAccountFormValues[];
    isValid: boolean;
    isDirty: boolean;
    overviewErrors: FormikErrors<SupplierDetailOverviewFormValues>;
    bankAccountErrors: FormikErrors<BankAccountFormValues>;
    /**
     * The supplier as the server last handed it over (#2159).
     *
     * `supplier` cannot serve as the unsaved-changes baseline: leaving a tab
     * parks its input right there, so the two become the same thing and nothing
     * reads as unsaved any more. This is only written when the server answers.
     */
    baseline?: SupplierDetailOverviewFormValues;
}

const initialSupplierReducerState: SupplierDetailOverviewReducerState = {
    whitelistedCustomer: { limit: 10, page: 0, total: 0, items: [] },
    bankAccounts: [],
    isValid: true,
    isDirty: false,
    overviewErrors: {},
    bankAccountErrors: {},
};

/**
 * Applies a change the server already has to both copies. Without the baseline
 * following along, uploading a picture or locking the supplier would leave the
 * mask calling itself unsaved for the rest of the session.
 */
const alsoOnServer = (
    state: SupplierDetailOverviewReducerState,
    change: Partial<SupplierDetailOverviewFormValues>
): SupplierDetailOverviewReducerState => ({
    ...state,
    supplier: state.supplier ? { ...state.supplier, ...change } : state.supplier,
    baseline: state.baseline ? { ...state.baseline, ...change } : state.baseline,
});

export const supplierDetailOverviewReducer = (
    state: SupplierDetailOverviewReducerState = initialSupplierReducerState,
    action: AnyAction
): SupplierDetailOverviewReducerState => {
    switch (action.type) {
        case SUPPLIER_DETAIL_OVERVIEW_RECEIVE_DETAILS: {
            return {
                ...state,
                supplier: action['supplier'],
                // The same values, kept apart from the ones the tabs edit.
                baseline: action['supplier'],
                bankAccounts: action['bankAccounts'],
                overviewErrors: {},
            };
        }

        case SUPPLIER_DETAIL_OVERVIEW_ERROR: {
            const formErrors: FormikErrors<SupplierDetailOverviewFormValues> = { ...state.overviewErrors };
            (action['errorData'] as HandledError<EmailErrorCode>[])?.forEach(error => {
                lodashSet(formErrors, error.property, EmailErrorCodeMessageId[error.error]);
            });

            return {
                ...state,
                isValid: false,
                overviewErrors: formErrors,
            };
        }

        case SUPPLIER_DETAIL_BANK_ACCOUNT_ERROR: {
            const formErrors: FormikErrors<BankAccountFormValues> = { ...state.bankAccountErrors };
            (action['errorData'] as HandledError<BankAccountErrorCode>[])?.forEach(error => {
                lodashSet(formErrors, error.property, BankAccountErrorCodeMessageId[error.error]);
            });

            return {
                ...state,
                bankAccountErrors: formErrors,
            };
        }
        case SUPPLIER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED: {
            const media: Media = action['media'];
            const duplicatedBankAccounts = [...state.bankAccounts];
            const index = duplicatedBankAccounts.findIndex(account => account.id === action['bankAccountId']);
            if (index >= 0) {
                duplicatedBankAccounts[index].media = [...duplicatedBankAccounts[index].media, media];
            }

            return {
                ...state,
                bankAccounts: duplicatedBankAccounts,
            };
        }

        case SUPPLIER_DETAIL_BANK_ACCOUNT_DELETE_FINISHED: {
            return {
                ...state,
                bankAccounts: state.bankAccounts.filter(bankAccount => bankAccount.id !== action['id']),
                bankAccountErrors: {},
            };
        }

        case SUPPLIER_DETAIL_BANK_ACCOUNT_UPDATED: {
            const duplicatedBankAccounts = [...state.bankAccounts];
            const index = duplicatedBankAccounts.findIndex(account => account.id === action['bankAccount'].id);

            if (index >= 0) {
                duplicatedBankAccounts[index] = action['bankAccount'];
            }

            return {
                ...state,
                bankAccounts: duplicatedBankAccounts,
                bankAccountErrors: {},
            };
        }

        case SUPPLIER_DETAIL_BANK_ACCOUNT_CREATED: {
            return {
                ...state,
                bankAccounts: [...state.bankAccounts, action['newBankAccount']],
                bankAccountErrors: {},
            };
        }

        case SUPPLIER_DETAIL_FTP_USER_CLEAR: {
            if (!state.supplier?.ftpUsername) {
                return state;
            }

            return alsoOnServer(state, { ftpUsername: undefined, ftpPassword: undefined });
        }

        case SUPPLIER_DETAIL_OVERVIEW_UPDATE: {
            const { formData, isDirty, isValid, errors } = action;

            const nextSupplier = {
                ...state.supplier,
                ...formData,
            };

            return {
                ...state,
                supplier: nextSupplier,
                isDirty: state.isDirty || isDirty,
                isValid,
                overviewErrors: errors,
            };
        }

        case SUPPLIER_DETAIL_WHITELISTED_CUSTOMERS_RECEIVE: {
            return {
                ...state,
                whitelistedCustomer: action['whitelistedCustomer'],
            };
        }

        case SUPPLIER_DETAIL_OVERVIEW_UPLOAD_PROFILE_FINISHED: {
            if (!state.supplier) {
                return state;
            }

            return alsoOnServer(state, { image: (action['media'] as Media).pathname });
        }

        case SUPPLIER_DETAIL_OVERVIEW_REMOVED_PROFILE_IMAGE: {
            if (!state.supplier) {
                return state;
            }

            return alsoOnServer(state, { image: undefined });
        }

        case SUPPLIER_DETAIL_TOGGLE_HIDE_PRODUCTS: {
            if (!state.supplier) {
                return state;
            }

            return alsoOnServer(state, { hideProducts: !state.supplier.hideProducts });
        }

        case SUPPLIER_DETAIL_TOGGLE_LOCK_SUPPLIER: {
            if (!state.supplier) {
                return state;
            }

            return alsoOnServer(state, { isLocked: !state.supplier.isLocked });
        }

        case SUPPLIER_DETAIL_NEW_FTP_PASSWORD_SAVE_SUCCESS: {
            if (!state.supplier) {
                return state;
            }

            return alsoOnServer(state, { ftpPassword: action['newFtpPassword'], newFtpPassword: '' });
        }

        case SUPPLIER_DETAIL_NEW_FTP_PASSWORD_SAVE: {
            if (!state.supplier) {
                return state;
            }

            return {
                ...state,
                supplier: {
                    ...state.supplier,
                    ftpPassword: action['newFtpPassword'],
                },
            };
        }

        case SUPPLIER_DETAIL_FTP_ADD_SUCCESS: {
            if (!state.supplier) {
                return state;
            }

            return alsoOnServer(state, {
                ftpPassword: action['ftpPassword'],
                ftpUsername: action['ftpUser'].ftpUsername,
            });
        }

        default: {
            return state;
        }
    }
};
